import React, { useEffect, useState, useMemo } from "react";
import VideosList from "./VideosList";
import img from "../../helpers/placeholder.jpg";
import { getMapMarkerVideos, searchVideos } from "../../api/videos/videosApi";
import { Video } from "../common/ObjectTypes";
import { useNavigate } from "react-router-dom";
import { MoonLoader } from "react-spinners";
import { PHOTO_URL } from "../../base";
import { getCountries } from "../../api/countries/countriesApi";
import { getSpecies } from "../../api/species/speciesApi";
import { getVideoResolutions } from "../../api/videoResolutions/videoResolutionsApi";
import { getVideoStatuses } from "../../api/videoStatuses/videoStatusesApi";
import { getOrganizations } from "../../api/organizations/organizationsApi";
import { getTags } from "../../api/tags/tagsApi";

type VideoManagementPageProps = {
  selectVideosPage: boolean;
  isMapMarkerVideoIncluded?: Function | undefined;
  onChangeVideoSelection?: Function;
  onSelectVideoForCatalogue?: Function;
  isCatalogueVideoIncluded?: Function | undefined;
  onSelectVideoForMapMarker?: Function;
  onSelectAllVideos?: Function;
  areAllVideosSelected?: boolean;
  isLoadingForCatalogueSelectAll?: boolean;
  catalogueMapMarkers?: any[];
  isDisabled?: boolean;
};

const VideoManagementPage: React.FC<VideoManagementPageProps> = ({
  selectVideosPage,
  isMapMarkerVideoIncluded,
  onChangeVideoSelection,
  catalogueMapMarkers,
  isCatalogueVideoIncluded,
  onSelectVideoForCatalogue,
  onSelectVideoForMapMarker,
  onSelectAllVideos,
  areAllVideosSelected,
  isLoadingForCatalogueSelectAll,
  isDisabled,
}) => {
  const navigate = useNavigate();
  const photoUrl = PHOTO_URL || "placeholder.jpg";
  const [videos, setVideos] = useState<Video[]>([]);
  const [countries, setCountries] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [species, setSpecies] = useState([]);
  const [tags, setTags] = useState([]);
  const [videoResolutions, setVideoResolutions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState<Video[]>([]);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [reloadPage, setReloadPage] = useState(true);
  const [query, setQuery] = useState({
    name: "",
    overview: "",
    countryObtainedId: null,
    speciesIds: [],
    videoResolutionId: 0,
    videoStatusIds: [],
    mapMarkerIds: [],
    tagIds: [],
  });
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [recordStart, setRecordStart] = useState(0);
  const [recordEnd, setRecordEnd] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 25;
  const [isModalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState({
    name: "",
    pageNumber: 1,
    pageSize,
  });
  const mapMarkerIds = useMemo(
    () => catalogueMapMarkers?.map((mapMarker) => mapMarker.mapMarkerId) || [],
    [catalogueMapMarkers]
  );

  // Generate a unique key based on areAllVideosSelected and current time to force re-render
  // This ensures thumbnails are reloaded when returning to this page
  const videosListKey = `videos-list-${
    areAllVideosSelected ? "selected" : "not-selected"
  }-${Date.now()}`;

  useEffect(() => {
    const loadVideos = async () => {
      setLoading(true);

      try {
        const temp = await searchVideos(searchQuery, mapMarkerIds);
        setVideos(temp.results);
        setFilteredVideos(temp.results);
        setPageCount(Math.ceil(temp.totalCount / searchQuery.pageSize));
        setTotalCount(temp.totalCount);

        const start = (searchQuery.pageNumber - 1) * searchQuery.pageSize + 1;
        const end = Math.min(start + temp.results.length - 1, temp.totalCount);

        setRecordStart(start);
        setRecordEnd(end);

        setPageLoaded(true);
        setReloadPage(false);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    const loadCountries = async () => {
      try {
        const temp = await getCountries(1000);
        setCountries(temp);
        setPageLoaded(true);
      } catch (err) {
        console.log(err);
      }
    };

    const loadSpecies = async () => {
      try {
        const temp = await getSpecies(1000);
        setSpecies(temp);
        setPageLoaded(true);
      } catch (err) {
        console.log(err);
      }
    };

    const loadVideoResolutions = async () => {
      try {
        const temp = await getVideoResolutions();
        setVideoResolutions(temp);
        setPageLoaded(true);
      } catch (err) {
        console.log(err);
      }
    };

    const loadVideoStatuses = async () => {
      try {
        const temp = await getVideoStatuses();
        setStatuses(temp);
        setPageLoaded(true);
      } catch (err) {
        console.log(err);
      }
    };
    const loadOrganizations = async () => {
      try {
        const temp = await getOrganizations();
        setOrganizations(temp);
        setPageLoaded(true);
      } catch (err) {
        console.log(err);
      }
    };

    const loadTags = async () => {
      try {
        const temp = await getTags(1000);
        setTags(temp);
        setPageLoaded(true);
      } catch (err) {
        console.log(err);
      }
    };

    if (!pageLoaded) {
      loadCountries();
      loadSpecies();
      loadVideoResolutions();
      loadVideoStatuses();
      loadOrganizations();
      loadTags();
    }
    if (reloadPage) loadVideos();
  }, [searchQuery, reloadPage, mapMarkerIds, pageLoaded]);

  // Add an effect to force thumbnail refresh when navigating back to this page
  useEffect(() => {
    // Generate a new timestamp to force image refresh when component mounts
    localStorage.setItem("thumbnail_last_updated", Date.now().toString());
  }, []);

  const handlePageClick = (selectedItem: { selected: number }) => {
    setSearchQuery((prevQuery) => ({
      ...prevQuery,
      pageNumber: selectedItem.selected + 1,
    }));
    setReloadPage(true);
  };

  const handleSelectVideo = (id: number) => {
    navigate("/video/" + id + "/edit");
  };

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery((prevQuery) => ({
      ...prevQuery,
      [event.target.name]: event.target.value,
    }));
  };

  const handleSelectCountry = (countryId: any) => {
    setQuery((prevQuery) => ({
      ...prevQuery,
      countryObtainedId: countryId || null,
    }));
  };
  const handleSelectResolution = (resId: any) => {
    setQuery((prevQuery) => ({
      ...prevQuery,
      videoResolutionId: resId || null,
    }));
  };

  const handleSelectTags = (selectedOptions: any) => {
    const selectedTagIds = selectedOptions ? selectedOptions : [];
    setQuery((prevQuery) => ({
      ...prevQuery,
      tagIds: selectedTagIds,
    }));
  };

  const handleSelectStatus = (selectedOptions: any) => {
    const selectedStatusIds = selectedOptions ? selectedOptions : [];
    setQuery((prevQuery) => ({
      ...prevQuery,
      videoStatusIds: selectedStatusIds,
    }));
  };

  // change query species
  const handleChangeSpecies = (selectedOptions: any) => {
    const selectedSpeciesIds = selectedOptions ? selectedOptions : [];
    setQuery((prevQuery) => ({
      ...prevQuery,
      speciesIds: selectedSpeciesIds,
    }));
  };

  const handleSearch = () => {
    setSearchQuery((prevQuery) => {
      const updatedQuery: any = {
        ...prevQuery,
        name: query.name || "",
        overview: query.overview || "",
        pageNumber: 1,
      };

      if (query.countryObtainedId) {
        updatedQuery.countryObtainedId = query.countryObtainedId;
      } else updatedQuery.countryObtainedId = null;

      if (query.videoResolutionId) {
        updatedQuery.videoResolutionId = query.videoResolutionId;
      } else updatedQuery.videoResolutionId = null;

      if (query.speciesIds && query.speciesIds.length > 0) {
        updatedQuery.speciesIds = query.speciesIds;
      } else updatedQuery.speciesIds = [];
      if (query.videoStatusIds && query.videoStatusIds.length > 0) {
        updatedQuery.videoStatusIds = query.videoStatusIds;
      } else updatedQuery.videoStatusIds = [];
      if (query.mapMarkerIds && query.mapMarkerIds.length > 0) {
        updatedQuery.mapMarkerIds = query.mapMarkerIds;
      } else updatedQuery.mapMarkerIds = [];
      if (query.tagIds && query.tagIds.length > 0) {
        updatedQuery.tagIds = query.tagIds;
      } else updatedQuery.tagIds = [];
      return updatedQuery;
    });
    handleCloseModal();

    setReloadPage(true);
  };

  const handleReset = () => {
    setQuery({
      name: "",
      overview: "",
      countryObtainedId: null,
      speciesIds: [],
      videoResolutionId: 0,
      videoStatusIds: [],
      mapMarkerIds: [],
      tagIds: [],
    });
    setSearchQuery({
      name: "",
      pageNumber: 1,
      pageSize,
    });
  };

  const handleClear = () => {
    setQuery({
      name: "",
      overview: "",
      countryObtainedId: null,
      speciesIds: [],
      videoResolutionId: 0,
      videoStatusIds: [],
      mapMarkerIds: [],
      tagIds: [],
    });
    setSearchQuery({
      name: "",
      pageNumber: 1,
      pageSize,
    });

    setReloadPage(true);
  };

  const sortVideosName = (order: boolean) => {
    const tempVideos = [...videos].sort(order ? ascName : descName);
    setFilteredVideos(tempVideos);
  };

  const sortVideosDate = (order: boolean) => {
    const tempVideos = [...videos].sort(order ? ascDate : descDate);
    setFilteredVideos(tempVideos);
  };

  const sortVideosPublished = (order: boolean) => {
    const tempVideos = [...videos].sort(order ? ascPublished : descPublished);
    setFilteredVideos(tempVideos);
  };

  function ascName(videoA: any, videoB: any) {
    // Use toUpperCase() to ignore character casing
    const vA = videoA.name.toUpperCase();
    const vB = videoB.name.toUpperCase();
    let comparison = 0;
    if (vA < vB) {
      comparison = 1;
    } else if (vA > vB) {
      comparison = -1;
    }
    return comparison;
  }
  // HELPER FUNCTION TO SORT
  function descName(videoA: any, videoB: any) {
    // Use toUpperCase() to ignore character casing
    const vA = videoA.name.toUpperCase();
    const vB = videoB.name.toUpperCase();
    let comparison = 0;
    if (vA > vB) {
      comparison = 1;
    } else if (vA < vB) {
      comparison = -1;
    }
    return comparison;
  }

  const ascDate = (videoA: any, videoB: any) => {
    return (
      new Date(videoA.lastEdited).getTime() -
      new Date(videoB.lastEdited).getTime()
    );
  };

  const descDate = (videoA: any, videoB: any) => {
    return (
      new Date(videoB.lastEdited).getTime() -
      new Date(videoA.lastEdited).getTime()
    );
  };

  const ascPublished = (videoA: any, videoB: any) => {
    return (
      new Date(videoA.published).getTime() -
      new Date(videoB.published).getTime()
    );
  };

  const descPublished = (videoA: any, videoB: any) => {
    return (
      new Date(videoB.published).getTime() -
      new Date(videoA.published).getTime()
    );
  };

  //modal

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return !pageLoaded || reloadPage ? (
    <div className="flex justify-center items-center">
      <MoonLoader color="#9EB45B" />
    </div>
  ) : (
    <VideosList
      videoResults={filteredVideos}
      sortVideosName={sortVideosName}
      sortVideosDate={sortVideosDate}
      sortVideosPublished={sortVideosPublished}
      onChangeFilter={handleChangeFilter}
      onOpenSearchModal={handleOpenModal}
      onCloseSearchModal={handleCloseModal}
      isModalOpen={isModalOpen}
      onSelectCountry={handleSelectCountry}
      onChangeSpecies={handleChangeSpecies}
      onChangeResolution={handleSelectResolution}
      onChangeStatus={handleSelectStatus}
      onSearch={handleSearch}
      onSelectVideo={handleSelectVideo}
      subStr={query}
      pageCount={pageCount}
      pageSize={pageSize}
      totalCount={totalCount}
      handlePageClick={handlePageClick}
      reloadPage={reloadPage}
      recordStart={recordStart}
      recordEnd={recordEnd}
      selectVideosPage={selectVideosPage}
      photoUrl={photoUrl}
      onClear={handleClear}
      onReset={handleReset}
      isMapMarkerVideoIncluded={isMapMarkerVideoIncluded}
      onSelectVideoForMapMarker={onSelectVideoForMapMarker}
      onChangeVideoSelection={onChangeVideoSelection}
      isCatalogueVideoIncluded={isCatalogueVideoIncluded}
      onSelectVideoForCatalogue={onSelectVideoForCatalogue}
      onSelectAllVideos={onSelectAllVideos}
      areAllVideosSelected={areAllVideosSelected}
      isLoadingForCatalogueSelectAll={isLoadingForCatalogueSelectAll}
      countries={countries}
      species={species}
      resolutions={videoResolutions}
      statuses={statuses}
      searchQuery={searchQuery}
      loading={loading}
      organizations={organizations}
      isDisabled={isDisabled}
      tags={tags}
      onSelectTags={handleSelectTags}
    />
  );
};

export default VideoManagementPage;
